import {  
  useCallback,
  createContext,
  useContext,
  useState,
  useMemo,
} from "react";
import { useEffectOnce } from "react-use";

import { VwoCampaign, VwoContextValue } from "./types";

export const VwoContext = createContext<VwoContextValue | null>(null);

export const VwoContextProvider = ({ children }) => {
  // #############################################################################
  // Ready
  // #############################################################################
  const [ready, setReady] = useState<VwoContextValue["ready"]>(false);

  // #############################################################################
  // Campaigns
  // #############################################################################
  const [campaigns, setCampaigns] = useState<VwoContextValue["campaigns"]>([]);

  const handleSetCampaign = useCallback(
    (campaignId: string, variationId: string) => {
      // Verify vwo proxy object
      if (!window["_vwo_exp"] || typeof window["_vwo_exp"] !== "object") return;

      // Setting name/variation as is without checking enums
      const campaign: VwoCampaign = {
        name: window["_vwo_exp"][campaignId].name,
        variation: window["_vwo_exp"][campaignId].comb_n[variationId],
      };

      // Add the campaign if it's not found by name
      // Assume campaign info won't change mid-way...
      if (!campaigns.some((el) => el.name === campaign.name)) {
        setCampaigns([...campaigns, campaign]);
      }
    },
    [campaigns],
  );

  const getCampaign = useCallback<VwoContextValue["getCampaign"]>(
    (name) => {
      return campaigns.find((el) => el.name === name) ?? null;
    },
    [campaigns],
  );

  // #############################################################################
  // VWO events
  // #############################################################################
  useEffectOnce(() => {
    window["VWO"] = window["VWO"] || [];
    window["VWO"].push([
      "onVWOLoaded",
      () => {
        setReady(true);
      },
    ]);
    window["VWO"].push([
      "onVariationApplied",
      /** @see https://gist.github.com/VWO-maker/eb40a1349e111eb89b258662241e26ac */
      (data: unknown) => {
        // Verify data
        if (!data || !Array.isArray(data)) return;

        const [, campaignId, variationId] = data;

        // Verify ids
        if (typeof campaignId !== "string" || typeof variationId !== "string")
          return;

        // Call it on the next tick to ensure the object is set
        setTimeout(() => handleSetCampaign(campaignId, variationId), 0);
      },
    ]);
  });

  // #############################################################################
  // Result
  // #############################################################################
  const value = useMemo<VwoContextValue>(
    () => ({
      ready,
      campaigns,
      getCampaign,
    }),
    [campaigns, getCampaign, ready],
  );
  return <VwoContext.Provider value={value}>{children}</VwoContext.Provider>;
};

export function useVwo(): VwoContextValue {
  const context = useContext(VwoContext);
  if (context === null) {
    throw new Error("useVwo must be used within a VwoContextProvider");
  }
  return context;
}
